import { assign, createMachine } from "xstate";
import {
  deletePayment,
  fetchPaymentHistory,
} from "@nju/data-access-portal-payment";
import { info } from "@nju/data-access-portal-user";
import { ContractError } from "@nju/result";

interface IContext {
  userId: string | undefined;
  isPending: boolean;
  paymentId: string | undefined;
  isCreditCardPrimary: boolean;
  isCreditCardActiveMethod: boolean;
}

type IEvent =
  | { type: "ERROR" }
  | {
      type: "FETCH_DATA_SUCCESS";
      isPending: boolean;
      paymentId: string | undefined;
      isCreditCardActiveMethod: boolean;
    }
  | { type: "DELETE_PAYMENT" }
  | { type: "DELETE_PAYMENT_SUCCESS" }
  | { type: "DELETE_PAYMENT_ERROR" };

export const mainPaymentPageMachine = createMachine<IContext, IEvent>(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "loading",
    states: {
      loading: {
        invoke: {
          src: "fetchData",
        },
        on: {
          FETCH_DATA_SUCCESS: {
            actions: "setData",
            target: "checkIfRefresh",
          },
          DELETE_PAYMENT: {
            target: "deletePayment",
          },
          ERROR: {
            target: "error",
          },
        },
      },
      checkIfRefresh: {
        always: [
          {
            cond: "isPending",
            target: "reloading",
          },
          {
            cond: "isCreditCardActiveMethod",
            actions: "onHandleReloadPage",
            target: "ready",
          },
          {
            target: "ready",
          },
        ],
      },
      reloading: {
        after: { 5000: "loading" },
        on: {
          DELETE_PAYMENT: {
            target: "deletePayment",
          },
        },
      },
      deletePayment: {
        invoke: {
          src: "deleteData",
        },
        on: {
          DELETE_PAYMENT_SUCCESS: {
            actions: "onHandleReloadPage",
            target: "ready",
          },
          DELETE_PAYMENT_ERROR: {
            target: "error",
          },
        },
      },
      ready: {
        type: "final",
      },
      error: {
        type: "final",
      },
    },
  },
  {
    guards: {
      isPending: (context) => {
        return context.isPending;
      },
      isCreditCardActiveMethod: (context) => {
        return context.isCreditCardPrimary !== context.isCreditCardActiveMethod;
      },
    },
    actions: {
      setData: assign<IContext, IEvent>((context, event) => {
        if (event.type === "FETCH_DATA_SUCCESS") {
          return {
            isPending: event.isPending,
            paymentId: event.paymentId,
            isCreditCardActiveMethod: event.isCreditCardActiveMethod,
          };
        }

        return context;
      }),
    },
    services: {
      fetchData: ({ userId }) => {
        return async (callback) => {
          try {
            const result = (
              await fetchPaymentHistory({
                page: 0,
                paymentsPerPage: 15,
                payerId: userId,
                type: ["REGISTER"],
                methodType: "CREDIT_CARD",
                status: "PENDING",
              })
            ).unwrap();
            const user = (await info()).unwrap();

            const isCreditCardActiveMethod =
              user.primaryPaymentMethod?.methodType === "CREDIT_CARD";

            const pendingCard = result.payments.find(({ status }) => {
              return status === "PENDING";
            });

            const isPending = pendingCard?.status === "PENDING";
            const paymentId = pendingCard?.id;

            return callback({
              type: "FETCH_DATA_SUCCESS",
              paymentId,
              isPending,
              isCreditCardActiveMethod,
            });
          } catch {
            callback({ type: "ERROR" });
          }
        };
      },
      deleteData: ({ paymentId }) => {
        return async (callback) => {
          try {
            if (!paymentId) {
              throw new ContractError({
                message: "Missing `paymentId`",
              });
            }

            await deletePayment({ paymentId });

            return callback({
              type: "DELETE_PAYMENT_SUCCESS",
            });
          } catch {
            return callback({ type: "DELETE_PAYMENT_ERROR" });
          }
        };
      },
    },
  },
);
